import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { getCampaignHasBeenDismissed } from "web/script/utils/block-modal-campaigns";
import storage from "web/script/utils/storage";
import { URLQuery } from "web/script/utils/url";
import { KevelCampaignControlSerializer } from "web/types/serializers";

type CampaignType = "top_banner" | "branch_link" | "block_modal";

export interface CampaignOptions {
    isIcon?: boolean;
    hasBeenBlocked?: boolean;
    hasBeenDismissed?: boolean;
    action?: string;
    isAffiliate?: boolean;
    hasPromotion?: boolean;
    retailerExcludedFromApp?: boolean;
    heimdallFlags?: string[];
    areaClicked?: string;
    productId?: number;
    linkId?: number | null;
    currency?: string | null;
    retailerSlug?: string | null;
    newUserGenerated?: boolean;
    pageCount?: number;
}

export interface CampaignDetails {
    titleKey?: string | null;
    componentType: string;
    ctaKey?: string | null;
}

export interface KevelCampaignControl {
    isKevelEnabled: boolean;
    campaignDetails: CampaignDetails | undefined;
    onClick: () => void;
    onLoad: () => void;
    fetchCampaign: () => Promise<CampaignDetails | undefined>;
}

function getCampaignDetails(data: KevelCampaignControlSerializer): CampaignDetails {
    return {
        titleKey: data.title_key,
        componentType: data.component_type,
        ctaKey: data.cta_key,
    };
}

export function useKevelCampaignControl(
    campaignType: CampaignType,
    options: CampaignOptions = {},
    fetchImmediately = true,
    contextType = ""
): KevelCampaignControl {
    const endpoint = "modules/kevel_campaign_control";
    const isKevelEnabled =
        Boolean(environment.getFeature("web_membership_treatment")) ||
        Boolean(environment.getFeature("web_membership_treatment_desktop"));

    function _getQuery(): URLQuery {
        const query: URLQuery = {
            campaignType: campaignType,
            contextType: contextType,
            feedType: environment.get("feedType"),
            pageType: environment.get("pageType"),
            pageSubType: environment.get("pageSubType"),
            initialPageType: analytics.getInitialPageType(),
            initialPageSubType: analytics.getInitialSubPageType(),
            continueClicked: storage.get("continueClicked", false, true),
            pageCount: storage.get("valid_journey_page_count", 0, true),
            ...new URLSearchParams(Object.entries(options)).toJSON(),
        };

        if (contextType) {
            const { hasBeenDismissed, dismissedCount } = getCampaignHasBeenDismissed(contextType);
            query.hasBeenDismissed = hasBeenDismissed.toString();
            query.dismissedCount = dismissedCount.toString();
        }

        return query;
    }

    function convertDataToCampaignDetails(
        data: KevelCampaignControlSerializer | undefined
    ): CampaignDetails | undefined {
        if (data) {
            return getCampaignDetails(data);
        }
    }

    const initialQuery = _getQuery();
    const { data, run } = useRothko<KevelCampaignControlSerializer>(endpoint, initialQuery, {
        enabled: isKevelEnabled && fetchImmediately,
        rothko: { cache: true },
    });

    let campaignDetails = convertDataToCampaignDetails(data);

    function onClick(): void {}

    function onLoad(): void {}

    async function fetchCampaign(): Promise<CampaignDetails | undefined> {
        if (isKevelEnabled) {
            const query = _getQuery();
            const data: KevelCampaignControlSerializer | undefined = await run(query);
            return convertDataToCampaignDetails(data);
        }
    }

    return {
        isKevelEnabled,
        campaignDetails,
        onClick,
        onLoad,
        fetchCampaign,
    };
}
